import * as fromAbout from 'mk2/apps/about/reducers';
import * as fromBazaar from 'mk2/apps/bazaar/reducers';
import * as fromBlogs from 'mk2/apps/blogs/reducers';
import * as fromBo from 'mk2/apps/bo/reducers';
import { DashAppState } from 'mk2/apps/dash/reducers';
import * as fromForum from 'mk2/apps/forum/reducers';
import * as fromHearts from 'mk2/apps/hearts/reducers';
import * as fromHome from 'mk2/apps/home/reducers';
import * as fromMail from 'mk2/apps/mail/reducers';
import * as fromSettings from 'mk2/apps/settings/reducers';
import { StrollersAppState } from 'mk2/apps/strollers/reducers';
import * as fromUsers from 'mk2/apps/users/reducers';
import { WallAppState } from 'mk2/apps/wall/reducers';
import { WikiAppState } from 'mk2/apps/wiki/reducers';
import ab, * as fromAB from 'mk2/containers/AB/AB.reducers';
import * as fromActionButton from 'mk2/containers/ActionButton/ActionButton.reducers';
import adSlot, * as fromAdSlot from 'mk2/containers/AdSlot/AdSlot.reducers';
import complaint, * as fromComplaint from 'mk2/containers/Complaint/Complaint.reducers';
import * as fromLike from 'mk2/containers/Like/Like.reducers';
import menu, * as fromMenu from 'mk2/containers/Menu/Menu.reducers';
import * as fromPageHeader from 'mk2/containers/PageHeader/PageHeader.reducers';
import phoneVerification, * as fromPhoneVerification from 'mk2/containers/PhoneVerification/PhoneVerification.reducers';
import phoneVerificationModal, * as fromPhoneVerificationModal from 'mk2/containers/PhoneVerificationModal/PhoneVerificationModal.reducers';
import * as fromStar from 'mk2/containers/Star/Star.reducers';
import * as fromToolbar from 'mk2/containers/Toolbar/Toolbar.reducers';
import { isIOSDevice } from 'mk2/helpers/detects';
import * as fromMKReduxForms from 'mk2/helpers/form.reducers';
import * as fromEntities from 'mk2/reducers/entities';
import request, * as fromRequest from 'mk2/reducers/request';
import response, * as fromResponse from 'mk2/reducers/response';
import { Entity } from 'mk2/schemas';
import { reducer as toastrReducer } from 'react-redux-toastr';
import { routerReducer, RouterState } from 'react-router-redux';
import { createSelector } from 'reselect';

export interface AppState {
    routing: RouterState;
    form: fromMKReduxForms.MKReduxFormsState;
    toastr: any; // TODO @type: Add react-router-toastr

    request: fromRequest.RequestState;
    response: fromResponse.ResponseState;

    entities: fromEntities.EntitiesState;
    containers: {
        global: {
            ab: fromAB.State;
            adSlot: fromAdSlot.AdSlotState;
            complaint: fromComplaint.ComplaintState;
            toolbar: fromToolbar.ToolbarsState;
            menu: fromMenu.MenuState;
            pageHeader: fromPageHeader.PageHeaderState;
            phoneVerification: fromPhoneVerification.PhoneVerificationState;
            phoneVerificationModal: fromPhoneVerificationModal.PhoneVerificationModalState;
            actionButtons: fromActionButton.ActionButtonsState;
            // newsletterModal: fromNewsletterModal.NewsletterModalState;
        },
        about: fromAbout.State;
        bazaar: fromBazaar.State;
        bo: fromBo.BoAppState;
        dash: DashAppState;
        mail: fromMail.MailAppState;
        wall: WallAppState;
        home: fromHome.HomeAppState;
        hearts: fromHearts.HeartsAppState;
        forum: fromForum.ForumAppState;
        blogs: fromBlogs.BlogsAppState;
        settings: fromSettings.SettingsAppState;
        wiki: WikiAppState;
        strollers: StrollersAppState;
        users: fromUsers.UsersAppState;
    };
}

const reducers = {
    // Libs
    routing: routerReducer,
    form: fromMKReduxForms.mkReduxFormsReducer,
    toastr: toastrReducer,

    // Ours
    request,
    response,

    entities: fromEntities.entitiesReducer,

    containers: {
        global: {
            ab,
            adSlot,
            complaint,
            toolbar: fromToolbar.toolbarsReducer,
            menu,
            pageHeader: fromPageHeader.pageHeaderReducer,
            phoneVerification,
            phoneVerificationModal,
            actionButtons: fromActionButton.actionButtonsReducer,
            // newsletterModal,
        },
        forum: fromForum.forumReducer,
        blogs: fromBlogs.blogsReducer,
    },
};

export default reducers;

// Export selectors from request
export const getRequestBaseUrl = (state: AppState) => fromRequest.getBaseUrl(state.request);
export const getRequestUser = (state: AppState) => fromRequest.getUser(state.request);
export const getRequestPermissions = (state: AppState) => fromRequest.getPermissions(state.request);
export const getRequestUserAgent = (state: AppState) => fromRequest.getUserAgent(state.request);
export const getRequestDevice = (state: AppState) => fromRequest.getDevice(state.request);
export const getRequestDeviceViewport = (state: AppState) => fromRequest.getDeviceViewport(state.request);
export const getRequestDeviceMobile = (state: AppState) => fromRequest.getDeviceMobile(state.request);
export const getRequestDeviceDesktop = (state: AppState) => fromRequest.getDeviceDesktop(state.request);
export const getRequestDeviceKeyboard = (state: AppState) => fromRequest.getDeviceKeyboard(state.request);
export const getRequestPhoneVerificationMode = (state: AppState) => fromRequest.getPhoneVerificationMode(state.request);
export const getRequestDeviceIsIOS = (state: AppState) => isIOSDevice(getRequestUserAgent(state));
export const getRequestLastKnownUsername = (state: AppState): string => state.request.lastKnownUsername;
export const getRequestPrivacyLeaflet = (state: AppState) => fromRequest.getPrivacyLeaflet(state.request);
// export const getNewsletterModalSubscribed = (state: AppState) => fromRequest.getPregnancyNewsletterSubscribed(state.request);
// export const getNewsletterModalClosed = (state: AppState) => fromRequest.getPregnancyNewsletterClosed(state.request);

export const getActionButtonState = (state: AppState, buttonId: string): fromActionButton.ActionButtonState =>
    state.containers.global.actionButtons[buttonId] || fromActionButton.initialActionButtonState;

// Export selectors from response
export const getResponseRenderedAt = (state: AppState) => fromResponse.getRenderedAt(state.response);

// Export selectors from entities
export const getEntitiesEntity = (state: AppState, entity: string, id: number | string): Entity => fromEntities.getEntity(state.entities, entity, id);

export const getFormState = (state: AppState, formName: string): fromMKReduxForms.MKReduxFormState =>
    state.form[formName] || fromMKReduxForms.initialMKReduxFormState;

// Export selectors from star
// Note: We are reading state from entities, because that is where the starrable enttiy is storred
export const getStarIsStarred = (state: AppState, entity: string, id: number) => fromStar.getIsStarred(state.entities.starrable as fromStar.StarrableEntitiesState, entity, id);

// Export selectors from complaint
export const getComplaintShow: (state: AppState, entity: string, id: number) => boolean = createSelector(
    [
        (state: AppState, entity: string, id: number) => state.containers.global.complaint,
        (state: AppState, entity: string, id: number) => entity,
        (state: AppState, entity: string, id: number) => id,
    ],
    (complaintState: fromComplaint.ComplaintState, entity: string, id: number) => fromComplaint.getShow(complaintState, entity, id),
);

// Export selectors from like
// Note: We are reading state from entities, because that is where the likeable enttiy is storred
export const getLikeIsLiked = (state: AppState, entity: string, id: number) => fromLike.getIsLiked(state.entities.likeable as fromLike.LikeableEntitiesState, entity, id);
export const getLikeLikesCount = (state: AppState, entity: string, id: number) => fromLike.getLikesCount(state.entities.likeable as fromLike.LikeableEntitiesState, entity, id);

// Export selectors from toolbar
export const getToolbarState = (state: AppState, formName: string): fromToolbar.ToolbarState =>
    state.containers.global.toolbar[formName] || fromToolbar.initialToolbarState;

// Export selectors from menu
export const getMenuShow = (state: AppState, menuId: string) => fromMenu.getShow(state.containers.global.menu, menuId);

// Forum
export const getPageHeaderState = (state: AppState): fromPageHeader.PageHeaderState => state.containers.global.pageHeader;

// Export selectors from phoneVerification
export const getPhoneVerificationStep = (state: AppState) => fromPhoneVerification.getStep(state.containers.global.phoneVerification);
export const getPhoneVerificationPrefix = (state: AppState) => fromPhoneVerification.getPrefix(state.containers.global.phoneVerification);
export const getPhoneVerificationPhoneNumber = (state: AppState) => fromPhoneVerification.getPhoneNumber(state.containers.global.phoneVerification);
export const getPhoneVerificationLimitReached = (state: AppState) => fromPhoneVerification.getLimitReached(state.containers.global.phoneVerification);
export const getPhoneVerificationSending = (state: AppState) => fromPhoneVerification.getSending(state.containers.global.phoneVerification);

// Export selectors from phoneVerificationModal
export const getPhoneVerificationModalOpened = (state: AppState) => fromPhoneVerificationModal.getOpened(state.containers.global.phoneVerificationModal);
export const getPhoneVerificationModalMode = (state: AppState) => fromPhoneVerificationModal.getMode(state.containers.global.phoneVerificationModal);

// Export selectors from newsletterModal
// export const getNewsletterModalOpened = (state: AppState) => fromNewsletterModal.getOpened(state.containers.global.newsletterModal);
// export const getNewsletterModalSubmitted = (state: AppState) => fromNewsletterModal.getSubmitted(state.containers.global.newsletterModal);
